import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  FileText, 
  MessageSquare, 
  LogOut, 
  Home,
  Menu,
  X,
  Building2
} from 'lucide-react';
import { useState } from 'react';
import { Button } from './ui/button';

const AdminLayout = () => {
  const { logout, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    { name: 'Dashboard', path: '/admin', icon: LayoutDashboard },
    { name: 'Biens à Vendre', path: '/admin/biens', icon: Building2 },
    { name: 'Articles', path: '/admin/blogs', icon: FileText },
    { name: 'Messages', path: '/admin/messages', icon: MessageSquare },
  ];

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 w-64 admin-sidebar transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="admin-sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-[#2a4a6f]">
            <img 
              src="https://customer-assets.emergentagent.com/job_beeb63f4-f271-4462-96ad-3cfca96ff85b/artifacts/c5rpsa8q_division%20immobiliere.jpeg" 
              alt="G Division" 
              className="h-12 w-auto bg-white p-1"
            />
            <p className="text-gray-400 text-xs mt-2">Administration</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                  isActive(item.path)
                    ? 'bg-[#c9a227] text-white'
                    : 'text-gray-300 hover:bg-[#2a4a6f] hover:text-white'
                }`}
                data-testid={`admin-nav-${item.name.toLowerCase()}`}
              >
                <item.icon size={20} />
                <span className="font-medium">{item.name}</span>
              </Link>
            ))}
          </nav>

          {/* Bottom Actions */}
          <div className="p-4 border-t border-[#2a4a6f] space-y-2">
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-[#2a4a6f] hover:text-white rounded-sm transition-colors"
              data-testid="admin-view-site"
            >
              <Home size={20} />
              <span>Voir le site</span>
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-red-600 hover:text-white rounded-sm transition-colors w-full"
              data-testid="admin-logout-btn"
            >
              <LogOut size={20} />
              <span>Déconnexion</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        {/* Top Bar */}
        <header className="bg-white shadow-sm h-16 flex items-center justify-between px-4 lg:px-8">
          <button
            className="lg:hidden p-2 text-[#1e3a5f]"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="admin-menu-toggle"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          
          <div className="flex items-center gap-4 ml-auto">
            <span className="text-sm text-gray-600">
              {user?.email}
            </span>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
